// app-components/investor-profile/co-investment-network/PartnerCard.jsx
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import InvestorLogo from '../../../../components/data/InvestorLogo';
import { Badge } from '../../../../components/ui/badge';
import HoverHint from '../../../../components/ui/hoverHint';
import { getAmountRangeByCode } from '../../../../lib/amountBins';
import { buildInvestorProfilePath, getLanguagePrefix } from '../../../../components/investor-display/helpers';
import { useMembershipAccess } from '../../../../hooks/membership';

const metricGridClass = "grid grid-cols-[repeat(auto-fit,minmax(min(100%,7.5rem),1fr))] gap-3 mb-4";
const metricCardClass = "min-w-0 text-center bg-gray-50 p-3 rounded-md";
const metricLabelClass = "flex flex-wrap items-center justify-center gap-1.5 text-sm leading-snug text-gray-500 break-words";
const hintClass = "w-4 h-4 shrink-0 bg-gray-400 text-white rounded-full flex items-center justify-center cursor-help";

const PartnerCard = ({ partner, partnerNames, dealInfo, showRecentIds = true }) => {
    const { t, i18n } = useTranslation();
    const { canSeeCoInvestDetails: canSeeDetails } = useMembershipAccess();

    // Stage codes mapping
    const stageLabels = {
        0: t('pageInvProf.stageUnknown'),
        1: t('pageInvProf.stagePreSeed'),
        2: t('pageInvProf.stageSeed'),
        3: t('pageInvProf.stageSeriesA'),
        4: t('pageInvProf.stageSeriesB'),
        5: t('pageInvProf.stageSeriesC'),
        6: t('pageInvProf.stageGrowth'),
        7: t('pageInvProf.stagePE'),
        8: t('pageInvProf.stageIPO')
    };

    // Amount codes mapping uses the shared amountBins.js helper.

    // Generate link for partner investor
    const generatePartnerLink = (partnerId) => {
        const partnerData = partnerNames[partnerId];
        if (!partnerData || !partnerData.id || !partnerData.name) {
            const prefix = getLanguagePrefix(i18n.language);
            return `${prefix}/app/investor-profile/${partnerId}`;
        }

        return buildInvestorProfilePath({
            investor: partnerData,
            language: i18n.language,
            inApp: true,
        });
    };

    // Format recency score (0-10 to percentage)
    const formatRecency = (recency) => {
        return Math.round((recency / 10) * 100);
    };

    // Format weight in basis points to percentage
    const formatWeight = (weightBps) => {
        return (weightBps / 100).toFixed(1);
    };

    // Format date to "April 3, 2025" format
    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Format large numbers to 12k, 12m, 1b format
    const formatAmount = (amount, currency = '') => {
        if (!amount || amount === 0) return `${currency}0`;

        const num = Number(amount);
        if (num >= 1e9) {
            return `${currency}${(num / 1e9).toFixed(1)}b`;
        } else if (num >= 1e6) {
            return `${currency}${(num / 1e6).toFixed(1)}m`;
        } else if (num >= 1e3) {
            return `${currency}${(num / 1e3).toFixed(1)}k`;
        } else {
            return `${currency}${num.toLocaleString()}`;
        }
    };

    return (
        <div className="min-w-0 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
            {/* Partner Header */}
            <div className="flex min-w-0 items-start gap-3 mb-4">
                <div className="flex shrink-0 items-center">
                    <InvestorLogo
                        id={partner.pid}
                        className="w-6 h-6"
                        alt="Partner logo"
                    />
                </div>
                <Link
                    to={generatePartnerLink(partner.pid)}
                    className="min-w-0 inline-flex flex-wrap items-center gap-1 text-lg font-bold text-gray-900 hover:underline whitespace-normal break-words leading-tight"
                >
                    <span className="min-w-0 break-words">{partnerNames[partner.pid]?.name || `Partner ${String(partner.pid).slice(-8)}`}</span>
                    <ExternalLink size={14} className="shrink-0" />
                </Link>
            </div>

            {/* Partner Stats */}
            <div className={metricGridClass}>
                <div className={metricCardClass}>
                    <div className={metricLabelClass}>
                        <span className="min-w-0">{t('pageInvProf.jointDeals')}</span>
                        <HoverHint message={t('pageInvProf.jointDealsHint')}>
                            <div className={hintClass}>
                                <span className="text-xs font-bold">?</span>
                            </div>
                        </HoverHint>
                    </div>
                    <div className="text-xl font-semibold text-gray-900">{partner.deals}</div>
                </div>

                <div className={metricCardClass}>
                    <div className={metricLabelClass}>
                        <span className="min-w-0">{t('pageInvProf.leadPercentage')}</span>
                        <HoverHint message={t('pageInvProf.leadPercentageHint')}>
                            <div className={hintClass}>
                                <span className="text-xs font-bold">?</span>
                            </div>
                        </HoverHint>
                    </div>
                    <div className="text-xl font-semibold text-gray-900">{partner.lead_pct}%</div>
                </div>

                <div className={metricCardClass}>
                    <div className={metricLabelClass}>
                        <span className="min-w-0">{t('pageInvProf.recency')}</span>
                        <HoverHint message={t('pageInvProf.recencyHint')}>
                            <div className={hintClass}>
                                <span className="text-xs font-bold">?</span>
                            </div>
                        </HoverHint>
                    </div>
                    <div className="text-xl font-semibold text-gray-900">{formatRecency(partner.recency)}%</div>
                </div>

                <div className={metricCardClass}>
                    <div className={metricLabelClass}>
                        <span className="min-w-0">{t('pageInvProf.weight')}</span>
                        <HoverHint message={t('pageInvProf.weightHint')}>
                            <div className={hintClass}>
                                <span className="text-xs font-bold">?</span>
                            </div>
                        </HoverHint>
                    </div>
                    <div className="text-xl font-semibold text-gray-900">{formatWeight(partner.weight_bps)}%</div>
                </div>
            </div>

            {/* Additional Info */}
            <div className="space-y-3">
            {/* Years Distribution */}
            {partner.years && partner.years.length > 0 && (
                <div>
                    <div className="text-sm font-medium text-gray-700 mb-2">{t('pageInvProf.recentYears')}</div>
                    <div className="flex flex-wrap gap-2">
                        {partner.years.map((year, idx) => (
                            canSeeDetails ? (
                                <Badge key={idx} text={`${year.y}: ${year.n}`} variant="v1" />
                            ) : (
                                <div key={idx} className="flex items-center gap-1">
                                    <Badge text={`${year.y}: ***`} variant="v1" />
                                    <HoverHint message={t('pageInvProf.startupProfessionalOnlyHint')}>
                                        <div className="w-4 h-4 bg-gray-400 text-white rounded-full flex items-center justify-center cursor-help">
                                            <span className="text-xs font-bold">?</span>
                                        </div>
                                    </HoverHint>
                                </div>
                            )
                        ))}
                    </div>
                </div>
            )}

                {/* Stages Distribution */}
                {partner.stages && partner.stages.length > 0 && (
                    <div>
                        <div className="text-sm font-medium text-gray-700 mb-2">
                            {t('pageInvProf.stages')}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {partner.stages.map((stage, idx) => {
                                const label = stage.label || stageLabels[stage.code] || String(stage.code);
                                return canSeeDetails ? (
                                    <Badge key={idx} text={`${label}: ${stage.n}`} variant="v2" />
                                ) : (
                                    <div key={idx} className="flex items-center gap-1">
                                        <Badge text={`${label}: ***`} variant="v2" />
                                        <HoverHint message={t('pageInvProf.startupProfessionalOnlyHint')}>
                                            <div className="w-4 h-4 bg-gray-400 text-white rounded-full flex items-center justify-center cursor-help">
                                                <span className="text-xs font-bold">?</span>
                                            </div>
                                        </HoverHint>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Amounts Distribution */}
                {partner.amounts && partner.amounts.length > 0 && (
                    <div>
                        <div className="text-sm font-medium text-gray-700 mb-2">{t('pageInvProf.amounts')}</div>
                        <div className="flex flex-wrap gap-2">
                            {partner.amounts.map((amount, idx) => (
                                canSeeDetails ? (
                                    <Badge
                                        key={idx}
                                        text={`${getAmountRangeByCode(amount.code)}: ${amount.n}`}
                                        variant="v3"
                                    />
                                ) : (
                                    <div key={idx} className="flex items-center gap-1">
                                        <Badge
                                            text={`${getAmountRangeByCode(amount.code)}: ***`}
                                            variant="v3"
                                        />
                                        <HoverHint message={t('pageInvProf.startupProfessionalOnlyHint')}>
                                            <div className="w-4 h-4 bg-gray-400 text-white rounded-full flex items-center justify-center cursor-help">
                                                <span className="text-xs font-bold">?</span>
                                            </div>
                                        </HoverHint>
                                    </div>
                                )
                            ))}
                        </div>
                    </div>
                )}

                {/* Recent Deals */}
                {showRecentIds && partner.recent_ids && partner.recent_ids.length > 0 && (
                    <div className="p-3 rounded">
                        <div className="font-medium text-gray-700 mb-2">{t('pageInvProf.recentDealsWithPartner')}</div>
                        {canSeeDetails ? (
                            <div className="space-y-2">
                                {partner.recent_ids.map((deal, idx) => (
                                    <div key={idx} className="min-w-0 text-sm text-gray-600">
                                        <div className="grid grid-cols-1 sm:grid-cols-[max-content_minmax(0,1fr)] gap-x-3 gap-y-1">
                                            <span className="font-medium">{formatDate(deal.dt)}</span>
                                            {dealInfo[deal.id] && (
                                                <span className="min-w-0 text-xs text-gray-500 sm:text-right break-words">
                                                    {dealInfo[deal.id].investment_round || 'Round'} • {formatAmount(dealInfo[deal.id].am_usd, dealInfo[deal.id].investment_currency)}
                                                </span>
                                            )}
                                        </div>
                                        {dealInfo[deal.id] && (
                                            <div className="text-xs text-gray-500 mt-1 break-words">
                                                {dealInfo[deal.id].title || dealInfo[deal.id].company_name || 'Deal Title'}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-gray-600">
                                <div className="min-w-0">
                                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                                        <span>{t('pageInvProf.recentDealsCount', { count: partner.recent_ids.length })}</span>
                                        <HoverHint message={t('pageInvProf.startupProfessionalOnlyHint')}>
                                            <div className={hintClass}>
                                                <span className="text-xs font-bold">?</span>
                                            </div>
                                        </HoverHint>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PartnerCard;
